import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Paper,
  Progress,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import { IconFileUpload, IconX } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { api } from "../../api/client";
import { useCompanyOptions, useNames } from "../../api/hooks";
import type { DocumentSummary } from "../../api/types";
import { type UploadItem, uploadDocuments } from "../../api/upload";
import { formatBytes, formatDateTime } from "../../lib/format";

const ACCEPT = [MIME_TYPES.pdf, MIME_TYPES.docx, MIME_TYPES.xlsx, "application/octet-stream"];
const MAX_SIZE = 200 * 1024 * 1024;
const BUSY_STATUSES = new Set([
  "queued",
  "converting",
  "extracting",
  "ocr",
  "chunking",
  "embedding",
]);

interface RecentDoc extends DocumentSummary {
  originalName: string;
  sizeBytes: number;
  version: number;
  sourceKind: string;
  error: string | null;
  createdAt: string;
}

export function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const color =
    status === "ready"
      ? "green"
      : status === "failed"
        ? "red"
        : status === "superseded" || status === "deleted"
          ? "gray"
          : "blue";
  return (
    <Badge color={color} variant="light" size="sm">
      {t(`status.${status}`, status)}
    </Badge>
  );
}

export function UploadPage() {
  const { t } = useTranslation();
  const { lookups, company, docType } = useNames();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState<File[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const companyOptions = useCompanyOptions(companyId);
  const [docTypeId, setDocTypeId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [regNumber, setRegNumber] = useState("");
  const [docDate, setDocDate] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [results, setResults] = useState<UploadItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const recent = useQuery({
    queryKey: ["documents", "recent"],
    queryFn: () => api<{ items: RecentDoc[] }>("/api/documents", { query: { pageSize: 25 } }),
    refetchInterval: (q) =>
      q.state.data?.items.some((d) => BUSY_STATUSES.has(d.status)) ? 3000 : false,
  });

  const submit = async () => {
    if (files.length === 0) return;
    setError(null);
    setProgress(0);
    try {
      const r = await uploadDocuments(
        files,
        {
          companyId: companyId ? Number(companyId) : undefined,
          docTypeId: docTypeId ? Number(docTypeId) : undefined,
          title: files.length === 1 ? title.trim() || undefined : undefined,
          regNumber: files.length === 1 ? regNumber.trim() || undefined : undefined,
          docDate: docDate ?? undefined,
        },
        setProgress,
      );
      setResults(r.items);
      setFiles([]);
      setTitle("");
      setRegNumber("");
      notifications.show({
        message: t("upload.done", { count: r.registered }),
        color: r.registered ? "green" : "yellow",
      });
      void queryClient.invalidateQueries({ queryKey: ["documents"] }); // recent list + header badge
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setProgress(null);
    }
  };

  return (
    <Stack gap="md">
      <Title order={2}>{t("upload.title")}</Title>
      <Group align="flex-start" gap="md" wrap="wrap">
        <Stack gap="sm" style={{ flex: 2, minWidth: 320 }}>
          <Dropzone
            onDrop={(accepted) => setFiles((prev) => [...prev, ...accepted])}
            onReject={(rejected) =>
              notifications.show({
                message: t("upload.rejected", {
                  names: rejected.map((r) => r.file.name).join(", "),
                }),
                color: "red",
              })
            }
            accept={ACCEPT}
            maxSize={MAX_SIZE}
            multiple
            disabled={progress !== null}
          >
            <Group justify="center" gap="md" mih={120} style={{ pointerEvents: "none" }}>
              <IconFileUpload size={40} stroke={1.2} />
              <div>
                <Text size="lg">{t("upload.dropHere")}</Text>
                <Text size="sm" c="dimmed">
                  {t("upload.formats")}
                </Text>
              </div>
            </Group>
          </Dropzone>
          {files.length > 0 && (
            <Paper withBorder p="sm">
              <Stack gap={4}>
                {files.map((f, i) => (
                  <Group key={`${f.name}-${f.size}`} justify="space-between" wrap="nowrap">
                    <Text size="sm" lineClamp={1}>
                      {f.name}{" "}
                      <Text span c="dimmed">
                        ({formatBytes(f.size)})
                      </Text>
                    </Text>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="gray"
                      onClick={() => setFiles(files.filter((_, j) => j !== i))}
                      leftSection={<IconX size={12} />}
                    >
                      {t("common.remove")}
                    </Button>
                  </Group>
                ))}
              </Stack>
            </Paper>
          )}
          {progress !== null && <Progress value={Math.round(progress * 100)} animated />}
          {error && (
            <Alert color="red" title={t("common.error")}>
              {error}
            </Alert>
          )}
          {results.length > 0 && (
            <Paper withBorder p="sm">
              <Stack gap={4}>
                <Text fw={600} size="sm">
                  {t("upload.results")}
                </Text>
                {results.map((r) => (
                  <Group key={`${r.fileName}-${r.status}`} gap="xs" wrap="nowrap">
                    <Badge
                      size="sm"
                      variant="light"
                      color={
                        r.status === "registered"
                          ? "green"
                          : r.status === "duplicate"
                            ? "yellow"
                            : "red"
                      }
                    >
                      {t(`upload.status.${r.status}`)}
                    </Badge>
                    <Text size="sm" lineClamp={1} style={{ flex: 1 }}>
                      {r.fileName}
                    </Text>
                    {r.documentId && (
                      <Anchor component={Link} to={`/documents/${r.documentId}`} size="sm">
                        #{r.documentId}
                        {r.supersedesId ? ` (${t("upload.newVersion")})` : ""}
                      </Anchor>
                    )}
                    {r.message && (
                      <Text size="xs" c="dimmed">
                        {r.message}
                      </Text>
                    )}
                  </Group>
                ))}
              </Stack>
            </Paper>
          )}
        </Stack>
        <Paper withBorder p="md" style={{ flex: 1, minWidth: 280 }}>
          <Stack gap="sm">
            <Text fw={600}>{t("upload.metadata")}</Text>
            <Select
              label={t("document.company")}
              data={companyOptions}
              value={companyId}
              onChange={setCompanyId}
              placeholder={t("upload.companyDefault")}
              clearable
            />
            <Select
              label={t("document.type")}
              data={(lookups.data?.docTypes ?? []).map((d) => ({
                value: String(d.id),
                label: docType(d.id),
              }))}
              value={docTypeId}
              onChange={setDocTypeId}
              clearable
            />
            <TextInput
              label={t("document.title")}
              value={title}
              onChange={(e) => setTitle(e.currentTarget.value)}
              disabled={files.length > 1}
              description={files.length > 1 ? t("upload.singleOnly") : t("upload.titleHint")}
            />
            <TextInput
              label={t("document.regNumber")}
              value={regNumber}
              onChange={(e) => setRegNumber(e.currentTarget.value)}
              disabled={files.length > 1}
              description={files.length > 1 ? t("upload.singleOnly") : t("upload.regHint")}
            />
            <DateInput
              label={t("document.date")}
              value={docDate}
              onChange={setDocDate}
              valueFormat="DD.MM.YYYY"
              clearable
            />
            <Button
              onClick={submit}
              disabled={files.length === 0}
              loading={progress !== null}
              leftSection={<IconFileUpload size={16} />}
            >
              {t("upload.button", { count: files.length })}
            </Button>
          </Stack>
        </Paper>
      </Group>

      <Stack gap="xs">
        <Title order={4}>{t("upload.recent")}</Title>
        {recent.data && recent.data.items.length === 0 && (
          <Text size="sm" c="dimmed">
            {t("upload.recentEmpty")}
          </Text>
        )}
        <Table.ScrollContainer minWidth={640}>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("document.file")}</Table.Th>
                <Table.Th>{t("document.company")}</Table.Th>
                <Table.Th>{t("document.regNumber")}</Table.Th>
                <Table.Th>{t("upload.source")}</Table.Th>
                <Table.Th>{t("upload.added")}</Table.Th>
                <Table.Th miw={130}>{t("upload.state")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(recent.data?.items ?? []).map((d) => (
                <Table.Tr key={d.id}>
                  <Table.Td>
                    <Anchor component={Link} to={`/documents/${d.id}`} size="sm">
                      {d.title}
                    </Anchor>
                    <Text size="xs" c="dimmed">
                      {d.originalName} · {formatBytes(d.sizeBytes)} · {d.pageCount}{" "}
                      {t("upload.pagesShort")}
                    </Text>
                  </Table.Td>
                  <Table.Td>{company(d.companyId)}</Table.Td>
                  <Table.Td>{d.regNumber ?? ""}</Table.Td>
                  <Table.Td>{t(`upload.sourceKind.${d.sourceKind}`, d.sourceKind)}</Table.Td>
                  <Table.Td>{formatDateTime(d.createdAt)}</Table.Td>
                  <Table.Td>
                    <StatusBadge status={d.status} />
                    {d.error && (
                      <Text size="xs" c="red" lineClamp={2}>
                        {d.error}
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Stack>
    </Stack>
  );
}
